/*
  ml-email-drawer-glass — the glass email drawer (design_handoff_email_drawer §7).

  Same public contract as assets/monthlees-email-drawer.js: window.MonthleesEmailDrawer =
  { open, close }, and a delegated click on [data-monthlees-open-email-drawer] opens it. Only one
  drawer is ever on the page (layout/theme.liquid renders this section OR the old one).

  Open: unhide → aria-hidden false → lock scroll (html/body.monthlees-email-drawer-open) →
  remember the opener → .is-open next frame → focus the email input (or the success block).
  Close (× / Esc / backdrop): drop .is-open, hide after 320ms, return focus to the opener.
  Focus is trapped in the panel while open.

  The form is a normal POST (Shopify customer form: CSRF and the challenge page keep working).
  This only adds a client-side email check and the "Joining…" state. After a successful POST
  Shopify returns to this page with the success state rendered; the drawer auto-opens, and with
  a redirect delay it goes to the chosen page after that delay.

  Analytics (MonthleesAnalytics.track, never the email address): ml_email_drawer_open,
  ml_email_drawer_submit, ml_email_drawer_error, ml_email_drawer_success, ml_email_drawer_close.
*/
(function () {
  'use strict';

  var OPEN = 'is-open';
  var LOCK = 'monthlees-email-drawer-open';
  var ERR_TEXT = 'Enter a full email address, like you@example.com.';
  var opener = null;
  var source = '';

  function root() { return document.querySelector('.ml-edg[data-monthlees-email-drawer]'); }
  function design(r) { return (r || root() || { getAttribute: function () { return ''; } }).getAttribute('data-design') || ''; }

  function track(name, props) {
    try {
      if (window.MonthleesAnalytics && typeof window.MonthleesAnalytics.track === 'function') window.MonthleesAnalytics.track(name, props || {});
      else if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, props || {});
    } catch (e) { /* never break the page */ }
  }

  function focusables(r) {
    var panel = r.querySelector('.ml-edg__panel');
    if (!panel) return [];
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.closest('[hidden]') && el.getClientRects().length > 0; }
    );
  }

  function open(opts) {
    var r = root();
    if (!r || r.classList.contains(OPEN)) return;
    opts = opts || {};
    opener = opts.opener || (document.activeElement !== document.body ? document.activeElement : null);
    r.hidden = false;
    r.removeAttribute('hidden');
    r.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(LOCK);
    document.body.classList.add(LOCK);
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        r.classList.add(OPEN);
        var ok = r.querySelector('[data-edg-success]:not([hidden])');
        var target = ok || r.querySelector('[data-edg-input]') || r.querySelector('[data-med-close]');
        if (target) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
      });
    });
    if (!opts.silent) track('ml_email_drawer_open', { design: design(r), source: opts.source || source || window.location.pathname });
  }

  function close(method) {
    var r = root();
    if (!r || !r.classList.contains(OPEN)) return;
    r.classList.remove(OPEN);
    r.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(LOCK);
    document.body.classList.remove(LOCK);
    if (typeof method === 'string') track('ml_email_drawer_close', { design: design(r), method: method });
    window.setTimeout(function () {
      if (!r.classList.contains(OPEN)) { r.hidden = true; r.setAttribute('hidden', ''); }
    }, 320);
    var back = opener;
    opener = null;
    if (back && typeof back.focus === 'function' && document.contains(back)) {
      try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
    }
  }

  function showError(r, text) {
    var input = r.querySelector('[data-edg-input]');
    var err = r.querySelector('[data-edg-error]');
    if (input) input.setAttribute('aria-invalid', 'true');
    if (err) { err.textContent = text; err.hidden = false; }
  }
  function clearError(r) {
    var input = r.querySelector('[data-edg-input]');
    var err = r.querySelector('[data-edg-error]');
    if (input) input.removeAttribute('aria-invalid');
    if (err) { err.hidden = true; err.textContent = ''; }
  }

  function init(r) {
    if (!r || r.getAttribute('data-edg-ready') === 'true') return;
    r.setAttribute('data-edg-ready', 'true');

    r.addEventListener('click', function (e) {
      var c = e.target.closest && e.target.closest('[data-med-close]');
      if (!c) return;
      close(c.classList.contains('ml-edg__backdrop') ? 'backdrop' : 'x');
    });

    // on the document, so Esc and the trap work even when focus has fallen to <body>
    document.addEventListener('keydown', function (e) {
      if (!r.classList.contains(OPEN) || !document.contains(r)) return;
      if (e.key === 'Escape') { e.preventDefault(); close('esc'); return; }
      if (e.key !== 'Tab') return;
      var items = focusables(r);
      if (!items.length) { e.preventDefault(); return; }
      var first = items[0], last = items[items.length - 1], active = document.activeElement;
      var panel = r.querySelector('.ml-edg__panel');
      if (e.shiftKey && (active === first || !panel.contains(active))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || !panel.contains(active))) { e.preventDefault(); first.focus(); }
    });

    var form = r.querySelector('form');
    var input = r.querySelector('[data-edg-input]');
    if (input) input.addEventListener('input', function () { if (input.getAttribute('aria-invalid') === 'true' && input.checkValidity()) clearError(r); });
    var submitBtn = r.querySelector('[data-edg-submit]');
    function invalid() {
      return input && (!input.value || !input.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim()));
    }
    // Validate on the button's click, not on submit: Shopify's form protection takes over the
    // submit event of customer forms and re-submits them, so a submit-time check can't stop a
    // bad address. Pressing Enter in the field also arrives here (implicit submission clicks
    // the default button). A cancelled click never submits.
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        if (submitBtn.getAttribute('aria-busy') === 'true') { e.preventDefault(); return; }
        if (invalid()) {
          e.preventDefault();
          showError(r, ERR_TEXT);
          track('ml_email_drawer_error', { design: design(r), kind: 'client' });
          input.focus();
        }
      });
    }
    if (form) {
      form.addEventListener('submit', function (e) {
        if (invalid()) { e.preventDefault(); return; } // form.requestSubmit() paths
        clearError(r);
        var btn = r.querySelector('[data-edg-submit]');
        var label = r.querySelector('[data-edg-submit-label]');
        if (btn) {
          if (btn.getAttribute('aria-busy') === 'true') { e.preventDefault(); return; }
          btn.setAttribute('aria-busy', 'true');
          // disable after this tick so the submit itself still goes through
          window.setTimeout(function () { btn.disabled = true; }, 0);
        }
        if (label) label.textContent = label.getAttribute('data-busy-label') || label.textContent;
        track('ml_email_drawer_submit', { design: design(r) });
        // normal POST continues
      });
    }

    // server-rendered states
    var ok = r.querySelector('[data-edg-success][data-edg-success-shown]');
    var serverErr = r.querySelector('[data-edg-error]:not([hidden])');
    if (ok) {
      var url = ok.getAttribute('data-redirect-url');
      track('ml_email_drawer_success', { design: design(r), behaviour: url ? 'redirect' : 'message' });
      open({ silent: true });
      if (url) {
        var delay = parseInt(ok.getAttribute('data-redirect-delay') || '0', 10) || 0;
        window.setTimeout(function () { window.location.assign(url); }, delay);
      }
    } else if (serverErr && serverErr.textContent.trim()) {
      track('ml_email_drawer_error', { design: design(r), kind: 'server' });
      open({ silent: true });
    } else if (r.getAttribute('data-design-mode') === 'true') {
      window.setTimeout(function () { open({ silent: true }); }, 500);
    }
  }

  function boot() {
    init(root());
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-monthlees-open-email-drawer]');
      if (!t) return;
      e.preventDefault();
      source = t.getAttribute('data-source') || '';
      open({ opener: t, source: source || window.location.pathname });
    });
    // bfcache: a back navigation after submitting leaves the button busy
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var r = root(); if (!r) return;
      var btn = r.querySelector('[data-edg-submit]'); var label = r.querySelector('[data-edg-submit-label]');
      if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
      if (label && label.getAttribute('data-label')) label.textContent = label.getAttribute('data-label');
    });
    var l = root() && root().querySelector('[data-edg-submit-label]');
    if (l) l.setAttribute('data-label', l.textContent);
    window.MonthleesEmailDrawer = { open: function () { open(); }, close: function () { close(); } };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme Editor: re-render (design switch) and select/deselect
  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target && e.target.querySelector('.ml-edg[data-monthlees-email-drawer]');
    if (r) { init(r); open({ silent: true }); }
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.querySelector('.ml-edg[data-monthlees-email-drawer]')) open({ silent: true });
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e.target && e.target.querySelector('.ml-edg[data-monthlees-email-drawer]')) close();
  });
})();
